// Mini US map that fills in states as they're answered.
// Re-uses the Albers-projected SVG paths already in states.js.
// Pass `answers` — an array of { stateAbbr, isCorrect, isOpenEd } —
// and optionally `currentAbbr` for the in-progress question.

import { useMemo } from 'react';
import { STATES } from '../data/states.js';

function computeBounds() {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const s of STATES) {
    const nums = s.path.match(/-?\d+(?:\.\d+)?/g);
    if (!nums) continue;
    for (let i = 0; i < nums.length - 1; i += 2) {
      const x = parseFloat(nums[i]);
      const y = parseFloat(nums[i + 1]);
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  return { minX, minY, maxX, maxY };
}

const BOUNDS = computeBounds();
const PAD = 10;
const VIEWBOX = `${BOUNDS.minX - PAD} ${BOUNDS.minY - PAD} ${BOUNDS.maxX - BOUNDS.minX + PAD * 2} ${BOUNDS.maxY - BOUNDS.minY + PAD * 2}`;

export default function QuizMap({ answers = [], currentAbbr = null, compact = false }) {
  // Build a map of abbr -> answer result for fast lookup
  const byAbbr = useMemo(() => {
    const m = {};
    for (const a of answers) m[a.stateAbbr] = a;
    return m;
  }, [answers]);

  return (
    <div className={`quiz-map ${compact ? 'is-compact' : ''}`}>
      <svg
        viewBox={VIEWBOX}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="US map — states fill in as you answer"
        preserveAspectRatio="xMidYMid meet"
      >
        {STATES.map(s => {
          const result = byAbbr[s.abbr];
          let cls = 'qm-state';
          if (result) {
            if (result.isCorrect && result.isOpenEd) cls += ' is-correct-opened';
            else if (result.isCorrect) cls += ' is-correct';
            else cls += ' is-missed';
          } else if (currentAbbr === s.abbr) {
            cls += ' is-current';
          }
          return (
            <path
              key={s.abbr}
              className={cls}
              d={s.path}
            >
              <title>{s.name}{s.isOpenEd ? ' — OpenEd state' : ''}</title>
            </path>
          );
        })}
      </svg>
    </div>
  );
}
